import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { usePartyColors } from '../contexts/PartyColorContext';
import Panel from './Panel';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import Header from './Header';
import Footer from './Footer';
import HashtagDisplay from './HashtagDisplay';
import MeetingItem from './MeetingItem';
import CollapsibleAgendaItem from './CollapsibleAgendaItem';
import FilterSidebar from './FilterSidebar';
import { 
  extractUniqueHashtags, 
  extractUniqueSpeakers, 
  applyAllFilters, 
  getResultCounts 
} from '../utils/filterUtils';

const Profile = () => {
  const { speakerName, meetingId } = useParams();
  const navigate = useNavigate();
  const { getPartyColor } = usePartyColors();
  
  // Data states
  const [speaker, setSpeaker] = useState(null);
  const [allMeetings, setAllMeetings] = useState([]);
  const [allAgendaItems, setAllAgendaItems] = useState([]);
  const [allSpeeches, setAllSpeeches] = useState([]);
  const [speakerSpeeches, setSpeakerSpeeches] = useState([]);
  const [topHashtags, setTopHashtags] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    selectedHashtags: [],
    selectedSpeakers: [] // This will be filtered to exclude current speaker
  });

  // Fetch speaker details and all related data
  useEffect(() => {
    async function fetchSpeakerData() {
      try {
        setLoading(true);
        
        // Split the speaker name into first name and last name
        const nameParts = decodeURIComponent(speakerName).split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
        
        console.log('Looking up speaker:', { firstName, lastName, fullName: speakerName });
        
        // Try to fetch speaker details from parliament_members table
        const { data: speakerData, error: speakerError } = await supabase
          .from('parliament_members')
          .select('*')
          .ilike('first_name', `%${firstName}%`)
          .ilike('last_name', `%${lastName}%`);

        if (speakerError) {
          console.error('Error fetching from parliament_members:', speakerError);
        }

        // Fetch all speeches by this speaker
        const { data: speechesData, error: speechesError } = await supabase
          .from('speeches')
          .select('*')
          .ilike('speaker_first_name', `%${firstName}%`)
          .ilike('speaker_last_name', `%${lastName}%`)
          .order('time', { ascending: false });

        if (speechesError) {
          throw speechesError;
        }

        setSpeakerSpeeches(speechesData || []);

        // Set speaker info
        if (speakerData && speakerData.length > 0) {
          setSpeaker(speakerData[0]);
        } else if (speechesData && speechesData.length > 0) {
          // Create speaker from speech data if not found in parliament_members
          const firstSpeech = speechesData[0];
          setSpeaker({
            id: 'unknown',
            first_name: firstSpeech.speaker_first_name,
            last_name: firstSpeech.speaker_last_name,
            party: firstSpeech.speaker_party
          });
        } else {
          throw new Error(`Speaker "${firstName} ${lastName}" not found`);
        }

        // Fetch all meetings
        const { data: meetingsData, error: meetingsError } = await supabase
          .from('meetings')
          .select('*')
          .order('date_of_sitting', { ascending: false });

        if (meetingsError) throw meetingsError;

        // Fetch all agenda items
        const { data: agendaItemsData, error: agendaItemsError } = await supabase
          .from('agenda_items')
          .select('*')
          .order('item_number', { ascending: true });

        if (agendaItemsError) throw agendaItemsError;

        // Fetch all speeches for filtering purposes
        const { data: allSpeechesData, error: allSpeechesError } = await supabase
          .from('speeches')
          .select('*')
          .order('time', { ascending: true });

        if (allSpeechesError) throw allSpeechesError;

        setAllMeetings(meetingsData || []);
        setAllAgendaItems(agendaItemsData || []);
        setAllSpeeches(allSpeechesData || []);

        // Calculate top hashtags for this speaker (only from agenda items)
        const speakerAgendaItemIds = [...new Set((speechesData || []).map(speech => speech.agenda_item_id))];
        const speakerHashtags = [];
        
        speakerAgendaItemIds.forEach(agendaItemId => {
          const agendaItem = (agendaItemsData || []).find(item => item.id === agendaItemId);
          if (agendaItem && agendaItem.hashtags && Array.isArray(agendaItem.hashtags)) {
            speakerHashtags.push(...agendaItem.hashtags);
          }
        });

        // Count hashtag frequency and get top hashtags
        const hashtagCounts = {};
        speakerHashtags.forEach(hashtag => {
          hashtagCounts[hashtag] = (hashtagCounts[hashtag] || 0) + 1;
        });

        const topHashtagsList = Object.entries(hashtagCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 8)
          .map(([hashtag]) => hashtag);

        setTopHashtags(topHashtagsList);

      } catch (error) {
        console.error('Error fetching speaker data:', error);
        setError('Failed to fetch speaker data');
      } finally {
        setLoading(false);
      }
    }

    fetchSpeakerData();
  }, [speakerName]);

  // Filter data to only include meetings/agenda items where the speaker participated
  const speakerFilteredData = useMemo(() => {
    if (!speakerSpeeches.length) return { meetings: [], agendaItems: [], speeches: [] };

    // Get unique agenda item IDs where speaker spoke
    const speakerAgendaItemIds = [...new Set(speakerSpeeches.map(speech => speech.agenda_item_id))];
    
    // Get agenda items where speaker spoke
    const speakerAgendaItems = allAgendaItems.filter(item => 
      speakerAgendaItemIds.includes(item.id)
    );

    // Get unique meeting IDs where speaker spoke
    const speakerMeetingIds = [...new Set(speakerAgendaItems.map(item => item.meeting_id))];
    
    // Get meetings where speaker spoke
    const speakerMeetings = allMeetings.filter(meeting => 
      speakerMeetingIds.includes(meeting.id)
    );

    return {
      meetings: speakerMeetings,
      agendaItems: speakerAgendaItems,
      speeches: speakerSpeeches
    };
  }, [speakerSpeeches, allMeetings, allAgendaItems]);

  // Set selected meeting from URL when on mobile route
  useEffect(() => {
    if (meetingId && speakerFilteredData.meetings.length > 0) {
      const found = speakerFilteredData.meetings.find(m => String(m.id) === String(meetingId));
      setSelectedMeeting(found || null);
    }
  }, [meetingId, speakerFilteredData.meetings]);

  // Apply additional filters on top of speaker-specific data
  const filteredData = useMemo(() => {
    return applyAllFilters(
      speakerFilteredData.meetings, 
      speakerFilteredData.agendaItems, 
      speakerFilteredData.speeches, 
      filters
    );
  }, [speakerFilteredData, filters]);

  // Get filter options (excluding current speaker from speakers)
  const hashtagOptions = useMemo(() => {
    return extractUniqueHashtags(speakerFilteredData.meetings, speakerFilteredData.agendaItems);
  }, [speakerFilteredData.meetings, speakerFilteredData.agendaItems]);

  const speakerOptions = useMemo(() => {
    const allSpeakers = extractUniqueSpeakers(speakerFilteredData.speeches);
    // Filter out the current speaker
    const currentSpeakerName = speaker ? `${speaker.first_name} ${speaker.last_name}` : '';
    return allSpeakers.filter(speakerOption => speakerOption.value !== currentSpeakerName);
  }, [speakerFilteredData.speeches, speaker]);

  // Get result counts
  const resultCounts = useMemo(() => {
    return getResultCounts(filteredData);
  }, [filteredData]);

  // Get agenda items for selected meeting
  const agendaItems = useMemo(() => {
    if (!selectedMeeting) return [];
    return filteredData.agendaItems.filter(item => item.meeting_id === selectedMeeting.id);
  }, [selectedMeeting, filteredData.agendaItems]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      dateRange: { start: '', end: '' },
      selectedHashtags: [],
      selectedSpeakers: []
    });
  };

  // Handle hashtag click - add to filter
  const handleHashtagClick = (hashtag) => {
    const newSelectedHashtags = filters.selectedHashtags.includes(hashtag)
      ? filters.selectedHashtags.filter(h => h !== hashtag)
      : [...filters.selectedHashtags, hashtag];

    setFilters({
      ...filters,
      selectedHashtags: newSelectedHashtags
    });
  };

  const handleMeetingClick = (meeting) => {
    if (window.innerWidth < 768) {
      navigate(`/profile/${encodeURIComponent(speakerName)}/mobile/${meeting.id}`);
    } else {
      setSelectedMeeting(meeting);
    }
  };

  const handleMobileBack = () => {
    navigate(`/profile/${encodeURIComponent(speakerName)}`);
  };

  // Reset meeting selection when filters change
  useEffect(() => {
    if (selectedMeeting && !filteredData.meetings.find(m => m.id === selectedMeeting.id)) {
      setSelectedMeeting(null);
    }
  }, [filteredData, selectedMeeting]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-200">
        <Header />
        <div className="container mx-auto p-4">
          <div className="flex justify-between items-center mb-4">
            <Link to="/parliament-members" className="inline-flex items-center justify-center py-2 px-4 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-transparent dark:bg-transparent cursor-pointer transition gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500">
              &larr; Back to Parliament Members
            </Link>
          </div>
          <LoadingSpinner message="Loading speaker profile..." />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-200">
        <Header />
        <div className="container mx-auto p-4">
          <div className="flex justify-between items-center mb-4">
            <Link to="/parliament-members" className="inline-flex items-center justify-center py-2 px-4 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-transparent dark:bg-transparent cursor-pointer transition gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500">
              &larr; Back to Parliament Members
            </Link>
          </div>
          <ErrorMessage message={error} />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col transition-colors duration-200">
      <Header />
      
      <div className="flex flex-1 relative">
        {/* Mobile filter toggle */}
        <button
          onClick={() => setIsFilterOpen(true)}

          className="md:hidden fixed top-40 left-2 z-30 inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm"

        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Filters
        </button>

        {meetingId && (
          <button
            onClick={handleMobileBack}
            className="md:hidden fixed top-16 left-4 z-30 inline-flex items-center p-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm"
          >
            Back
          </button>
        )}
        {/* Filter Sidebar */}
        <FilterSidebar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          hashtagOptions={hashtagOptions}
          speakerOptions={speakerOptions}
          onClearFilters={handleClearFilters}
          resultCounts={resultCounts}
          isMobileOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />

        {isFilterOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setIsFilterOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
            <Link to="/parliament-members" className="inline-flex items-center justify-center py-2 px-4 text-sm font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-transparent dark:bg-transparent cursor-pointer transition gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500">
              &larr; Back to Parliament Members
            </Link>
          </div>
          
          <ErrorMessage message={error} />

          {/* Speaker Profile Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6 transition-colors duration-200">
            <div className="p-4 px-6 border-b border-gray-200 dark:border-gray-700 bg-primary text-white">
              <h2 className="text-xl font-semibold m-0">Speaker Profile</h2>
            </div>
            <div className="p-4 px-6">
              {speaker && (
                <div className="flex flex-col md:flex-row items-start md:items-center">
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
                      {speaker.first_name} {speaker.last_name}
                    </h1>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {speaker.party && (
                        <span
                          className="inline-flex items-center justify-center py-1 px-3 text-xs font-medium rounded-full uppercase tracking-wider text-white"
                          style={{ backgroundColor: getPartyColor(speaker.party) }}
                        >
                          {speaker.party}
                        </span>
                      )}
                    </div>
                    
                    {/* Top Topics Section */}
                    {topHashtags && topHashtags.length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Top Topics</h3>
                        <HashtagDisplay 
                          hashtags={topHashtags}
                          maxVisible={6}
                          onHashtagClick={handleHashtagClick}
                          className="flex-wrap"
                        />
                        {topHashtags.length > 6 && (
                          <span className="inline-flex items-center px-2 py-1 text-xs text-gray-500 dark:text-gray-400 mt-2">
                            +{topHashtags.length - 6} more topics
                          </span>
                        )}
                      </div>
                    )}

                    {/* Activity Stats */}
                    <div className="mt-4 flex items-center space-x-6 text-sm">
                      <div className="flex items-center space-x-1">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span className="text-gray-900 dark:text-white font-medium">{speakerSpeeches.length}</span>
                        <span className="text-gray-500 dark:text-gray-400">total speeches</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="text-gray-900 dark:text-white font-medium">{speakerFilteredData.meetings.length}</span>
                        <span className="text-gray-500 dark:text-gray-400">meetings participated</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Meetings and Agenda Items */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Meetings Panel */}
            {!(window.innerWidth < 768 && meetingId) && (
              <Panel title="Meetings">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredData.meetings.map((meeting) => (
                      <MeetingItem
                        key={meeting.id}
                        meeting={meeting}
                        isSelected={selectedMeeting?.id === meeting.id}
                        onClick={() => handleMeetingClick(meeting)}
                        onHashtagClick={handleHashtagClick}
                        agendaItems={filteredData.agendaItems}
                      />
                    ))}
                  {filteredData.meetings.length === 0 && (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      No meetings found for the selected filters.
                    </div>
                  )}
                </div>
              </Panel>
            )}

            {/* Agenda Items Panel */}
            <Panel title="Agenda Items">
              {!selectedMeeting ? (
                <div className="p-8 text-center">
                  <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">
                    Select a meeting
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    Choose a meeting from the left to view agenda items where {speaker?.first_name} {speaker?.last_name} spoke
                  </p>
                </div>
              ) : agendaItems.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No agenda items found for the selected meeting and filters.
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {agendaItems.map(agendaItem => {
                    // Get speeches for this agenda item by this speaker
                    const agendaItemSpeeches = speakerSpeeches.filter(speech => 
                      speech.agenda_item_id === agendaItem.id
                    );
                    
                    const agendaItemWithSpeeches = {
                      agendaItemInfo: {
                        id: agendaItem.id,
                        title: agendaItem.title || 'Unknown Agenda Item',
                        itemNumber: agendaItem.item_number || 0,
                        description: agendaItem.description,
                        hashtags: agendaItem.hashtags || [],
                        leaning_score: agendaItem.leaning_score
                      },
                      speeches: agendaItemSpeeches
                    };

                    return (
                      <CollapsibleAgendaItem 
                        key={agendaItem.id} 
                        agendaItem={agendaItemWithSpeeches}
                        onHashtagClick={handleHashtagClick}
                      />
                    );
                  })}
                </div>
              )}
            </Panel>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default Profile;
